import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import type { ClientSummary, ProjectSummary } from "@/lib/api";
import { getEffectiveStatus } from "@/lib/project-status";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function getInitial(name: string): string {
  return (name.trim()[0] ?? "?").toUpperCase();
}

function ColorSwatch({ label, color }: { label: string; color?: string | null }) {
  const valid = !!color && HEX_RE.test(color);
  return (
    <div className="flex items-center gap-1.5" title={label}>
      {valid ? (
        <span
          className="border-border size-4 shrink-0 rounded"
          style={{ backgroundColor: color }}
        />
      ) : (
        <span className="border-border bg-muted size-4 shrink-0 rounded border border-dashed" />
      )}
      <span className="text-muted-foreground text-xs">{valid ? color : "Not set"}</span>
    </div>
  );
}

interface ClientCardProps {
  client: ClientSummary;
  /** Projects already filtered down to this client (by the caller). */
  projects: ProjectSummary[];
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Card summarizing one client -- logo/initial avatar, brand swatches, project
 * count, and an expandable list of its projects. Edit/delete are delegated to
 * the caller-owned dialogs via `onEdit`/`onDelete`.
 */
export function ClientCard({ client, projects, onEdit, onDelete }: ClientCardProps) {
  const [expanded, setExpanded] = useState(false);
  // Count the projects this card can actually list on expand. `projects` is
  // already scoped to what the current user may see (`GET /projects` only
  // returns their own for non-admins), so using its length keeps the count and
  // the expandable list in agreement instead of showing an unlistable global
  // total from `client.project_count`.
  const projectCount = projects.length;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <Avatar size="lg" className="size-12 rounded-lg">
            {client.logo_url && <AvatarImage src={client.logo_url} alt="" />}
            <AvatarFallback className="bg-accent-light text-primary rounded-lg text-sm font-semibold">
              {getInitial(client.name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 className="text-foreground truncate text-base font-medium">{client.name}</h3>
            <p className="text-muted-foreground truncate text-xs">{client.slug}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        <div className="flex items-center gap-4">
          <ColorSwatch label="Primary color" color={client.primary_color} />
          <ColorSwatch label="Accent color" color={client.accent_color} />
        </div>
        <p className="text-muted-foreground text-xs">
          {projectCount} project{projectCount !== 1 ? "s" : ""}
        </p>

        {projects.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
            >
              {expanded ? (
                <ChevronDown className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
              {expanded ? "Hide projects" : "Show projects"}
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1.5">
                {projects.map((project) => {
                  const status = getEffectiveStatus(project);
                  return (
                    <li
                      key={project.id}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="text-foreground min-w-0 truncate">
                        {project.project_name}
                      </span>
                      <Badge variant={status.variant} className="shrink-0">
                        {status.label}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="mt-auto gap-2 border-t px-6 py-3">
        <Button variant="outline" size="sm" onClick={onEdit} className="flex-1">
          <Pencil className="mr-1.5 size-3.5" />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
        >
          <Trash2 className="size-3.5" />
          <span className="sr-only">Delete {client.name}</span>
        </Button>
      </CardFooter>
    </Card>
  );
}
